import { MessageType, ThreadWithLastMessage } from "@common/types/chat";
import classNames from "classnames";
import { useMemo } from "react";
import { useConstant } from "@hooks/useConstant";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./Chat.module.scss";
import { AuditLogsThreadPreview } from "./customMessages/AuditLogsMessage";
import { SlateRenderer } from "./slate/SlateRenderer";
import { isThreadUnread } from "./threadUnread";

interface Props {
    onClick: () => void;
    thread: ThreadWithLastMessage;
    workspaceName?: string;
}

export const Thread = ({ thread, workspaceName, onClick }: Props) => {
    const hasNewMessage = isThreadUnread(thread);
    const now = useConstant(() => new Date());

    const lastMessageAtLocalized = useMemo(() => {
        const d = thread.lastMessageAt ? new Date(thread.lastMessageAt) : null;
        if (!d || isNaN(d.getTime())) {
            return "";
        }

        const isToday =
            d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate();

        if (isToday) {
            return d.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
            });
        }

        const diffMs = now.getTime() - d.getTime();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        if (diffMs >= 0 && diffMs < sevenDaysMs) {
            return d.toLocaleDateString("en-US", { weekday: "short" }); // e.g., Mon, Tue
        }

        return d.toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
        }); // e.g., 7/20
    }, [thread.lastMessageAt, now]);

    return (
        <div className={styles.thread} onClick={onClick}>
            <VStack gap={4} className={styles.threadPreview}>
                {workspaceName && (
                    <span className={styles.threadWorkspaceName}>
                        {workspaceName}
                    </span>
                )}
                <span className={styles.threadTitle}>{thread.title}</span>
                {thread.lastMessage?.isDeleted && (
                    <span
                        className={classNames(
                            styles.lastMessage,
                            styles.deletedMessagePreview,
                        )}
                    >
                        This message was deleted
                    </span>
                )}
                {!thread.lastMessage?.isDeleted &&
                    thread.lastMessage?.content &&
                    thread.lastMessage?.type === MessageType.SLATE && (
                        <div className={styles.lastMessage}>
                            <SlateRenderer
                                content={thread.lastMessage.content}
                            />
                        </div>
                    )}
                {!thread.lastMessage?.isDeleted &&
                    thread.lastMessage?.content &&
                    thread.lastMessage?.type === MessageType.API && (
                        <span className={styles.lastMessage}>
                            {thread.lastMessage.content}
                        </span>
                    )}
                {!thread.lastMessage?.isDeleted &&
                    thread.lastMessage?.content &&
                    thread.lastMessage?.type === MessageType.AUDIT_LOGS && (
                        <span className={styles.lastMessage}>
                            <AuditLogsThreadPreview
                                content={thread.lastMessage.content}
                            />
                        </span>
                    )}
            </VStack>
            <div>
                <span
                    className={classNames(styles.unreadIndicator, {
                        [styles.new]: hasNewMessage,
                    })}
                >
                    {lastMessageAtLocalized}
                </span>
            </div>
        </div>
    );
};
